<script setup lang="ts">
import { X } from "lucide-vue-next";

const props = defineProps<{
  modelValue: boolean;
  title: string;
  description?: string;
  confirmText: string;
  cancelText: string;
  loading?: boolean;
  width?: "md" | "lg" | "xl";
}>();

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
  confirm: [];
}>();

let previousHtmlOverscrollBehavior = "";
let previousBodyOverscrollBehavior = "";

const panelWidthClass = computed(() => {
  return {
    md: "max-w-[520px]",
    lg: "max-w-[720px]",
    xl: "max-w-[860px]",
  }[props.width || "lg"];
});

function canScrollInsideDialog(target: EventTarget | null) {
  if (!(target instanceof Element)) return false;

  return !!target.closest("[data-scroll-lock-ignore]");
}

function preventScroll(event: Event) {
  if (canScrollInsideDialog(event.target)) return;

  event.preventDefault();
}

function preventScrollKeys(event: KeyboardEvent) {
  const blockedKeys = [
    "ArrowUp",
    "ArrowDown",
    "PageUp",
    "PageDown",
    "Home",
    "End",
    " ",
  ];

  if (blockedKeys.includes(event.key)) {
    if (canScrollInsideDialog(event.target)) return;

    event.preventDefault();
  }
}

function lockBodyScroll() {
  if (typeof document === "undefined") return;

  const html = document.documentElement;
  const body = document.body;

  previousHtmlOverscrollBehavior = html.style.overscrollBehavior;
  previousBodyOverscrollBehavior = body.style.overscrollBehavior;

  html.style.overscrollBehavior = "none";
  body.style.overscrollBehavior = "none";

  html.classList.add("app-dialog-scroll-lock");
  body.classList.add("app-dialog-scroll-lock");

  window.addEventListener("wheel", preventScroll, { passive: false });
  window.addEventListener("touchmove", preventScroll, { passive: false });
  window.addEventListener("keydown", preventScrollKeys, { passive: false });
}

function unlockBodyScroll() {
  if (typeof document === "undefined") return;

  const html = document.documentElement;
  const body = document.body;

  html.style.overscrollBehavior = previousHtmlOverscrollBehavior;
  body.style.overscrollBehavior = previousBodyOverscrollBehavior;

  html.classList.remove("app-dialog-scroll-lock");
  body.classList.remove("app-dialog-scroll-lock");

  window.removeEventListener("wheel", preventScroll);
  window.removeEventListener("touchmove", preventScroll);
  window.removeEventListener("keydown", preventScrollKeys);
}

function close() {
  if (props.loading) return;

  emit("update:modelValue", false);
}

function confirm() {
  if (props.loading) return;

  emit("confirm");
}

function handleEscPress(event: KeyboardEvent) {
  if (event.key === "Escape") {
    close();
  }
}

watch(
  () => props.modelValue,
  value => {
    if (value) {
      lockBodyScroll();
    } else {
      unlockBodyScroll();
    }
  },
);

onMounted(() => {
  window.addEventListener("keyup", handleEscPress);

  if (props.modelValue) {
    lockBodyScroll();
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("keyup", handleEscPress);
  unlockBodyScroll();
});
</script>

<template>
  <Teleport to="body">
    <Transition name="dialog-backdrop">
      <button
        v-if="modelValue"
        type="button"
        class="fixed inset-0 z-[100] bg-black/50"
        aria-label="Close dialog"
        @click="close"
      />
    </Transition>

    <Transition name="dialog-panel">
      <div
        v-if="modelValue"
        class="fixed inset-x-3 top-1/2 z-[101] mx-auto flex max-h-[calc(100dvh-50px)] md:max-h-[calc(100dvh-200px)] -translate-y-1/2 flex-col overflow-hidden rounded-2xl border border-app-border bg-white shadow-[0_24px_90px_rgba(15,23,42,0.32)] sm:inset-x-4"
        data-scroll-lock-ignore
        :class="panelWidthClass"
      >
        <button
          type="button"
          class="absolute right-5 top-5 flex size-8 items-center justify-center rounded-md text-app-muted transition hover:bg-gray-100 hover:text-app-text"
          @click="close"
        >
          <X class="size-5" />
        </button>

        <div
          class="flex shrink-0 gap-4 border-b border-app-border px-5 py-5 pr-14 sm:px-7"
        >
          <slot name="icon" />

          <div>
            <h2 class="text-2xl font-semibold leading-tight text-app-text">
              {{ title }}
            </h2>
            <p v-if="description" class="mt-1.5 text-base text-app-muted">
              {{ description }}
            </p>
          </div>
        </div>

        <div
          class="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-5 sm:px-7"
          data-scroll-lock-ignore
          @wheel.stop
          @touchmove.stop
        >
          <slot />
        </div>

        <div
          class="grid shrink-0 grid-cols-1 gap-3 border-t border-app-border bg-white px-5 py-4 sm:flex sm:justify-end sm:px-7"
        >
          <button
            type="button"
            class="flex w-full select-none items-center justify-center rounded-lg border border-app-border bg-white px-5 py-2.5 text-sm font-semibold text-app-text transition hover:bg-gray-100 sm:w-[96px]"
            @click="close"
          >
            {{ cancelText }}
          </button>

          <button
            type="button"
            :disabled="loading"
            class="flex w-full select-none items-center justify-center rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-400 disabled:cursor-not-allowed disabled:bg-primary-400 sm:w-[96px]"
            @click="confirm"
          >
            {{ confirmText }}
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.dialog-backdrop-enter-active,
.dialog-backdrop-leave-active {
  transition: opacity 160ms ease;
}

.dialog-backdrop-enter-from,
.dialog-backdrop-leave-to {
  opacity: 0;
}

.dialog-panel-enter-active,
.dialog-panel-leave-active {
  transition:
    opacity 180ms ease,
    transform 180ms ease;
}

.dialog-panel-enter-from,
.dialog-panel-leave-to {
  opacity: 0;
  transform: translateY(-46%) scale(0.98);
}
</style>
